import { OverlayProvider } from '@gluestack-ui/core/overlay/creator'
import { ToastProvider } from '@gluestack-ui/core/toast/creator'
import React from 'react'

import { NotificationHost } from './NotificationHost'

/**
 * Mounts the gluestack toast subsystem (overlay portal + toast context) plus the
 * {@link NotificationHost} bridge, so `notify()` / `useNotify()` work for anything
 * rendered inside `children`.
 *
 * Why this is separate from `GluestackUIProvider`: on mobile the app is split into
 * Module Federation chunks (an embedded `bootstrap` chunk vs the lazy `main-app`
 * chunk). `aos-ui-universal` is NOT a shared MF singleton, so the gluestack
 * `ToastContext` is a DIFFERENT module instance per chunk. `GluestackUIProvider`
 * (with its `ToastProvider`) lives in the bootstrap chunk, but the screens + sagas
 * that raise toasts live in `main-app` — so their `useToast()` reads the main-app
 * copy of `ToastContext`, which the bootstrap provider never populated, and
 * `show()` silently no-ops (render is never called). Wrapping the main-app root in
 * this provider gives those consumers a `ToastProvider` from THEIR OWN chunk.
 * Web has no chunk split and uses `GluestackUIProvider`'s `ToastProvider` directly.
 */
export function NotificationProvider({ children }: { children?: React.ReactNode }) {
    return (
        <OverlayProvider>
            <ToastProvider>
                <NotificationHost />
                {children}
            </ToastProvider>
        </OverlayProvider>
    )
}

NotificationProvider.displayName = 'NotificationProvider'
